import {
  ApplicationRef,
  ComponentRef,
  EmbeddedViewRef,
  Injectable,
  Type,
  createComponent
} from '@angular/core';

import { Observable } from 'rxjs';

import { DrawerComponent, DrawerComponentOptions } from './drawer.component';

// type Newable = { new (...args: readonly unknown[]): unknown };
// type AnyFn = (...args: unknown[]) => unknown;

// type ClassProperties<C extends Newable> = {
//   [K in keyof InstanceType<C> as InstanceType<C>[K] extends AnyFn
//     ? never
//     : K]: InstanceType<C>[K];
// };

interface DrawerProps<T> {
  id?: string;
  data?: T;
  title?: string;
  description?: string;
  options?: DrawerComponentOptions;
}

interface Result<R> {
  data: R;
  index: number;
}

export interface DrawerComponentRef<R> {
  closed$: Observable<Result<R>>;
  status$: Observable<'open' | 'closed'>;
}

class DrawerServiceResource {
  removeComponentFromBody<T = unknown>(
    appRef: ApplicationRef,
    component: ComponentRef<T>
  ): void {
    if (!component) {
      return;
    }

    appRef.detachView(component.hostView);
    component.destroy();

    component = undefined;
  }
}

const MAX_DRAWERS = 2;

@Injectable({
  providedIn: 'root'
})
export class DrawerService {
  private drawerComponentRefs: ComponentRef<DrawerComponent<any, any>>[] = [];

  private drawerServiceInternal: DrawerServiceResource;

  constructor(private appRef: ApplicationRef) {
    if (!this.drawerServiceInternal) {
      this.drawerServiceInternal = new DrawerServiceResource();
    }
  }

  // eslint-disable-next-line max-lines-per-function
  open<R, T = unknown>(
    component: Type<any>,
    props: DrawerProps<T> = {} as DrawerProps<T>
  ): DrawerComponentRef<R> {
    if (this.drawerComponentRefs.length >= MAX_DRAWERS) {
      console.error(`Maximum number of drawers reached ${MAX_DRAWERS}`);
      return void 0;
    }

    const drawerComponentRef = createComponent(DrawerComponent<T, R>, {
      environmentInjector: this.appRef.injector
    });

    this.#setCardInputs(drawerComponentRef, {
      component
    });
    drawerComponentRef.instance.resource = {
      service: this,
      removeComponentFromBody: () =>
        this.drawerServiceInternal.removeComponentFromBody(
          this.appRef,
          drawerComponentRef
        )
    };

    if (props.title) {
      this.#setCardInputs(drawerComponentRef, {
        title: props.title
      });
    }

    if (props.description) {
      this.#setCardInputs(drawerComponentRef, {
        description: props.description
      });
    }

    if (props.options) {
      this.#setCardInputs(drawerComponentRef, {
        options: props.options
      });
    }

    const { data, id } = props || {};

    const drawerIndex = this.drawerComponentRefs.length;
    this.#setCardInputs(drawerComponentRef, {
      drawerIndex
    });

    const componentId = id ?? Math.random().toString(36).substring(2);

    drawerComponentRef.instance.open(componentId, data || ({} as T));

    this.appRef.attachView(drawerComponentRef.hostView);

    const domElem = (drawerComponentRef.hostView as EmbeddedViewRef<T>)
      .rootNodes[0] as HTMLElement;
    document.body.appendChild(domElem);

    this.drawerComponentRefs.push(drawerComponentRef);

    return {
      closed$: drawerComponentRef.instance.closed$,
      status$: drawerComponentRef.instance.status$
    };
  }

  close<R>(result?: R): void {
    if (this.drawerComponentRefs.length === 0) {
      return;
    }

    const drawerComponentRef = this.drawerComponentRefs.pop();
    if (drawerComponentRef) {
      drawerComponentRef.instance.close(result);
    }
  }

  #setCardInputs(
    componentRef: ComponentRef<DrawerComponent>,
    inputs: Record<string, unknown>
  ) {
    Object.keys(inputs).forEach((key) => {
      if (key in componentRef.instance) {
        componentRef.setInput(key, inputs[key]);
      }
    });
  }
}
